import React, { useEffect, useMemo, useState, useRef } from 'react';
import { getRegionMeta } from '../utils/region';
import { motion, AnimatePresence, useInView, useScroll, useTransform, animate } from 'framer-motion';
import { HardHat, Truck, Clock, Phone, DollarSign, Calculator, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SMSOrderCTA } from '../components/SMSOrderCTA';
const benefits = [{
  icon: DollarSign,
  title: 'Bulk Pricing',
  description: 'Volume discounts on all materials and dedicated account pricing for our regular partners.'
}, {
  icon: Clock,
  title: 'Priority Scheduling',
  description: 'Same-day and next-day delivery slots reserved specifically for contractors to keep jobs moving.'
}, {
  icon: Phone,
  title: 'Dedicated Support',
  description: 'Direct line to our dispatch team. No waiting in queue when you need materials fast.'
}, {
  icon: CheckCircle2,
  title: 'Flexible Billing',
  description: 'Net-30 terms available for qualified contractors to help manage cash flow.'
}, {
  icon: Truck,
  title: 'Job Site Delivery',
  description: 'Professional drivers who know how to navigate tight job sites and drop exactly where needed.'
}, {
  icon: Calculator,
  title: 'Material Calculator',
  description: 'Free project estimation and material planning assistance for complex jobs.'
}];
const steps = [{
  number: '01',
  title: 'Apply',
  description: 'Fill out the quick application form below with your company details and estimated volume.'
}, {
  number: '02',
  title: 'Get Approved',
  description: 'Our team will review your application and set up your account within 24 hours.'
}, {
  number: '03',
  title: 'Start Ordering',
  description: 'Access your discounted pricing and priority scheduling immediately upon approval.'
}];
const discountTiers = [{
  volume: '24-50 yards',
  discount: '5% Off',
  targetValue: 5,
  desc: 'Mid-size project discount'
}, {
  volume: '50-100 yards',
  discount: '10% Off',
  targetValue: 10,
  desc: 'Large project discount'
}, {
  volume: '100+ yards',
  discount: 'Call Us',
  targetValue: null,
  desc: 'Custom bulk pricing'
}];
function RingTrace({
  elementSize,
  delay,
  duration,
  isInView,
  startAngle
}: {
  elementSize: number;
  delay: number;
  duration: number;
  isInView: boolean;
  startAngle: number;
}) {
  const svgSize = elementSize + 4;
  const radius = elementSize / 2 + 1;
  const circumference = 2 * Math.PI * radius;
  return <svg width={svgSize} height={svgSize} className="absolute pointer-events-none z-20" style={{
    top: '50%',
    left: '50%',
    transform: `translate(-50%, -50%) rotate(${startAngle}deg)`
  }} data-id="element-3229" data-mp="nmj33k0rnmsf">
      <motion.circle cx={svgSize / 2} cy={svgSize / 2} r={radius} fill="none" stroke="rgba(251, 191, 36, 0.3)" strokeWidth={2} strokeLinecap="round" initial={{
      strokeDasharray: circumference,
      strokeDashoffset: circumference
    }} animate={isInView ? {
      strokeDashoffset: 0
    } : {
      strokeDashoffset: circumference
    }} transition={{
      duration,
      ease: [0.25, 0.1, 0.25, 1],
      delay
    }} data-id="element-3230" data-mp="dsipetrf5983" />
    </svg>;
}
function AnimatedNumber({
  value
}: {
  value: number;
}) {
  const nodeRef = useRef<HTMLSpanElement>(null);
  const inView = useInView(nodeRef, {
    once: true,
    margin: '-50px'
  });
  useEffect(() => {
    if (inView && nodeRef.current) {
      const controls = animate(0, value, {
        duration: 1.2,
        ease: 'easeOut',
        onUpdate(v) {
          if (nodeRef.current) {
            nodeRef.current.textContent = Math.round(v).toString();
          }
        }
      });
      return () => controls.stop();
    }
  }, [inView, value]);
  return <span ref={nodeRef} data-id="element-3231" data-mp="3gxgtimsmuprx">0</span>;
}
export function Subcontractors() {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    phone: '',
    email: '',
    licenseNumber: '',
    estimatedVolume: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const stepsRef = useRef<HTMLDivElement>(null);
  const stepsInView = useInView(stepsRef, {
    once: true,
    margin: '-100px'
  });
  const {
    scrollY
  } = useScroll();
  const heroY = useTransform(scrollY, [0, 500], [0, 150]);
  const ringParams = useMemo(() => ({
    ringDelays: [0.5, 2.0, 0.8],
    ringDurations: [8, 10, 7],
    ringAngles: [45, 220, 310],
    mobileRingAngles: [120, 300, 30],
    lineDelays: [0, 1.5],
    lineDurations: [9, 11]
  }), []);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSubmitted(true);
    setIsSubmitting(false);
    setFormData({
      companyName: '',
      contactName: '',
      phone: '',
      email: '',
      licenseNumber: '',
      estimatedVolume: ''
    });
  };
  const inputClass = 'w-full bg-[#f5f4f1] dark:bg-[#3a3938] border border-stone-300 dark:border-[#525252] rounded-none px-4 py-3 text-sm text-stone-900 dark:text-white placeholder-stone-400 focus:ring-2 focus:ring-[#fbbf24]/50 focus:border-[#fbbf24]/50 focus:shadow-[0_0_0_4px_rgba(251,191,36,0.2)] outline-none transition-all';
  return <main className="min-h-screen bg-[#fdfbf7] dark:bg-[#2a2a2a] transition-colors duration-300 pb-20 relative overflow-hidden" data-id="element-3232" data-mp="14va8knljriyb">
      {/* Ambient Background Blobs (Dark Mode primarily) */}
      <div className="fixed inset-0 pointer-events-none z-0 hidden dark:block overflow-hidden" data-id="element-3233" data-mp="vyjh6ohxwsa2">
        <motion.div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#fbbf24] rounded-full mix-blend-screen opacity-[0.04] blur-[100px]" animate={{
        x: [0, 100, -50, 0],
        y: [0, -100, 50, 0]
      }} transition={{
        duration: 20,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-3234" data-mp="1004olubpgecv" />
        <motion.div className="absolute bottom-1/4 right-1/4 w-[30rem] h-[30rem] bg-[#fbbf24] rounded-full mix-blend-screen opacity-[0.05] blur-[120px]" animate={{
        x: [0, -150, 100, 0],
        y: [0, 100, -100, 0]
      }} transition={{
        duration: 25,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-3235" data-mp="22c669lj9i9xp" />
      </div>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden bg-[#2c2c2c]" data-id="element-3236" data-mp="1jh7lg446j8qk">
        <div className="absolute inset-0" data-id="element-3237" data-mp="1env8wscryse6">
          <motion.img src="https://cdn.magicpatterns.com/uploads/tn9AHnmsKjEsy7nQqUqaij/file_000000005a5471f5abe1156df7570912~2.png" alt="Concrete pour at foundation jobsite with crew" className="w-full h-full object-cover opacity-60 origin-center" animate={{
          scale: [1, 1.08, 1]
        }} transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-3238" data-mp="1emtpo0lluzld" />
          <div className="absolute inset-0 bg-black/30" data-id="element-3239" data-mp="vsen61034oss" />
        </div>
        <motion.div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" style={{
        y: heroY
      }} data-id="element-3240" data-mp="lllq4woo91cz">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="flex flex-col items-center justify-center mb-8" data-id="element-3241" data-mp="vkbb4ybv71xg">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-black/40 backdrop-blur-md border border-white/10 text-white/90 text-xs font-bold tracking-widest uppercase mb-6 shadow-lg" data-id="element-3242" data-mp="2wyn0jq22m21">
              <motion.span className="w-2 h-2 rounded-full bg-[#fbbf24]" animate={{
              opacity: [1, 0.4, 1]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-3243" data-mp="cs8tpkrefhm5" />
              Now Serving Pro Contractors
            </div>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#fbbf24]/10 backdrop-blur-sm border border-[#fbbf24]/20 shadow-[0_0_30px_rgba(251,191,36,0.2)]" data-id="element-3244" data-mp="3l7ip6a0s9oky">
              <HardHat className="w-8 h-8 text-[#fbbf24]" data-id="element-3245" data-mp="zax38demmcyu" />
            </div>
          </motion.div>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="font-heading text-4xl md:text-6xl font-extrabold text-white mb-6 tracking-tight" data-id="element-3246" data-mp="1da6lrjqb1j5z">
            Contractor <span className="text-[#fbbf24]" data-id="element-3247" data-mp="1i65dnhaudjb5">Program</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-xl text-stone-200 max-w-2xl mx-auto font-medium font-sub leading-relaxed" data-id="element-3248" data-mp="1vyl99x1eyf21">
            Partner with {`${getRegionMeta().code}Gravel`} for bulk pricing,
            priority scheduling, and dedicated support to keep your projects
            running smoothly and profitably.
          </motion.p>
        </motion.div>
      </section>

      {/* SMS Order CTA — text-first ordering for contractors */}
      <SMSOrderCTA data-id="element-3249" data-mp="2aog0llvoi87u" />

      {/* Volume Discount Tiers */}
      <section className="py-20 bg-[#Ffffff] dark:bg-[#474443] relative z-10" data-id="element-3250" data-mp="2fjvci8a4lu1m">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3251" data-mp="20w0imokdtjmy">
          <div className="text-center mb-12" data-id="element-3252" data-mp="25myyxo5dehq7">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 dark:text-white mb-4" data-id="element-3253" data-mp="25ns78tjz9fag">
              Volume Discount Tiers
            </h2>
            <p className="text-stone-600 dark:text-stone-300 text-lg" data-id="element-3254" data-mp="1wuf5r37ul5cr">
              Discounts applied automatically on qualifying orders.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto" data-id="element-3255" data-mp="1hzhbg4t525t1">
            {discountTiers.map((tier, index) => <motion.div key={tier.volume} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} whileHover={{
            y: -4
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="group relative bg-white dark:bg-[#525050] rounded-xl p-6 border border-stone-200 dark:border-[#5e5b5b] text-center hover:border-[#fbbf24]/50 hover:shadow-[0_8px_30px_rgba(251,191,36,0.2)] transition-all duration-300 overflow-hidden" data-id="element-3256" data-mp="1y007v9xoz8kc" data-mpi={index}>
                {/* Gradient Sheen */}
                <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 dark:via-white/5 to-transparent -skew-x-12 translate-x-[-150%]" variants={{
              hover: {
                translateX: '150%',
                transition: {
                  duration: 0.7,
                  ease: 'easeInOut'
                }
              }
            }} data-id="element-3257" data-mp="270yfs3b94087" />

                <div className="text-stone-700 dark:text-stone-200 font-bold mb-2 relative z-10" data-id="element-3258" data-mp="t0k65lbwsibr">
                  {tier.volume}
                </div>
                <div className="text-2xl font-heading font-extrabold text-[#f59e0b] dark:text-[#fbbf24] mb-2 relative z-10" data-id="element-3259" data-mp="o1gydt4jny03">
                  {tier.targetValue !== null ? <>
                      <AnimatedNumber value={tier.targetValue} data-id="element-3260" data-mp="xsv44in39u1o" />% Off
                    </> : tier.discount}
                </div>
                <div className="text-sm text-stone-600 dark:text-stone-200 relative z-10" data-id="element-3261" data-mp="a424oc79d42b">
                  {tier.desc}
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 bg-[#F5f4f1] dark:bg-[#474443] relative z-10" data-id="element-3262" data-mp="f0j5a5vn9awa">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3263" data-mp="1uht0g5ahm00y">
          <div className="text-center mb-16" data-id="element-3264" data-mp="1ufzrgjcej3ps">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 dark:text-white mb-4" data-id="element-3265" data-mp="1qghgafadu705">
              Program Benefits
            </h2>
            <p className="text-stone-600 dark:text-stone-300 text-lg" data-id="element-3266" data-mp="n524366yp2q5">
              Everything you need to scale your business.
            </p>
          </div>
          <div className="space-y-16" data-id="element-3267" data-mp="x382vexh1sqs">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-3268" data-mp="x49hftdp4xd9">
              {benefits.map((benefit, index) => <motion.div key={benefit.title} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} whileHover={{
              y: -4
            }} viewport={{
              once: true
            }} transition={{
              type: 'spring',
              stiffness: 80,
              delay: index * 0.1
            }} className="group relative bg-white dark:bg-[#525050] rounded-2xl p-8 border border-stone-200 dark:border-[#5e5b5b] hover:border-[#fbbf24]/40 hover:shadow-[inset_0_2px_10px_rgba(251,191,36,0.05),0_10px_30px_rgba(0,0,0,0.05)] dark:hover:shadow-[inset_0_2px_10px_rgba(251,191,36,0.1),0_10px_30px_rgba(0,0,0,0.25)] transition-all duration-300 overflow-hidden" data-id="element-3269" data-mp="112yzw0dld53r" data-mpi={index}>
                  <motion.div variants={{
                hover: {
                  scale: 1.1,
                  rotate: 4
                }
              }} transition={{
                type: 'spring',
                stiffness: 300,
                damping: 15
              }} className="inline-block mb-6 relative" data-id="element-3270" data-mp="39dzpxns4uo8v">
                    <benefit.icon className="w-10 h-10 text-[#f59e0b] dark:text-[#fbbf24] relative z-10" data-id="element-3271" data-mp="2vf038lo6kjfc" />
                    {/* Hairline accent under icon */}
                    <motion.div className="absolute -bottom-2 left-0 right-0 h-[2px] bg-[#fbbf24]/60 origin-left" variants={{
                  initial: {
                    scaleX: 0
                  },
                  hover: {
                    scaleX: 1
                  }
                }} initial="initial" data-id="element-3272" data-mp="2lo5glg70vhtd" />
                  </motion.div>
                  <h3 className="font-heading text-xl font-bold text-stone-900 dark:text-white mb-3" data-id="element-3273" data-mp="27pp2332x4n0o">
                    {benefit.title}
                  </h3>
                  <p className="text-stone-600 dark:text-stone-200 leading-relaxed" data-id="element-3274" data-mp="girgzhc49k94">
                    {benefit.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white dark:bg-[#2a2a2a] relative z-10" data-id="element-3275" data-mp="3oykxvxad0xqp">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3276" data-mp="3t0pr4uwwbsct">
          <div className="text-center mb-16" data-id="element-3277" data-mp="3t20tcqap0s1t">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 dark:text-white mb-4" data-id="element-3278" data-mp="3f16kk6jqgp2m">
              How to Join
            </h2>
            <p className="text-stone-600 dark:text-stone-300 text-lg" data-id="element-3279" data-mp="3k09p7pvswta2">
              A simple process to get you started.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 relative" ref={stepsRef} data-id="element-3280" data-mp="1i96busuj10lt">
            {/* Desktop Connecting Lines */}
            <div className="hidden md:block absolute top-12 left-[10%] right-[10%] h-[2px] bg-stone-200 dark:bg-[#525252]" data-id="element-3281" data-mp="32qyqz2n4b851">
              <motion.div initial={{
              width: '0%'
            }} animate={stepsInView ? {
              width: '50%'
            } : {
              width: '0%'
            }} transition={{
              duration: ringParams.lineDurations[0],
              ease: [0.25, 0.1, 0.25, 1],
              delay: ringParams.lineDelays[0]
            }} className="absolute inset-y-0 left-0 rounded-full" style={{
              backgroundColor: 'rgba(251, 191, 36, 0.6)',
              boxShadow: '0 0 4px rgba(251, 191, 36, 0.25)'
            }} data-id="element-3282" data-mp="2xprkd6wpoezy" />
              <motion.div initial={{
              width: '0%'
            }} animate={stepsInView ? {
              width: '50%'
            } : {
              width: '0%'
            }} transition={{
              duration: ringParams.lineDurations[1],
              ease: [0.25, 0.1, 0.25, 1],
              delay: ringParams.lineDelays[1]
            }} className="absolute inset-y-0 left-[50%] rounded-full" style={{
              backgroundColor: 'rgba(251, 191, 36, 0.6)',
              boxShadow: '0 0 4px rgba(251, 191, 36, 0.25)'
            }} data-id="element-3283" data-mp="3qhkwon9nggbv" />
            </div>

            {/* Mobile Connecting Lines */}
            <div className="md:hidden absolute top-0 bottom-0 left-8 w-[2px] bg-stone-200 dark:bg-[#525252]" data-id="element-3284" data-mp="2zrgqyziueo31">
              <motion.div initial={{
              height: '0%'
            }} animate={stepsInView ? {
              height: '50%'
            } : {
              height: '0%'
            }} transition={{
              duration: ringParams.lineDurations[0],
              ease: [0.25, 0.1, 0.25, 1],
              delay: ringParams.lineDelays[0]
            }} className="absolute inset-x-0 top-0 rounded-full" style={{
              backgroundColor: 'rgba(251, 191, 36, 0.6)',
              boxShadow: '0 0 4px rgba(251, 191, 36, 0.25)'
            }} data-id="element-3285" data-mp="34n5rp66lmb0x" />
              <motion.div initial={{
              height: '0%'
            }} animate={stepsInView ? {
              height: '50%'
            } : {
              height: '0%'
            }} transition={{
              duration: ringParams.lineDurations[1],
              ease: [0.25, 0.1, 0.25, 1],
              delay: ringParams.lineDelays[1]
            }} className="absolute inset-x-0 top-[50%] rounded-full" style={{
              backgroundColor: 'rgba(251, 191, 36, 0.6)',
              boxShadow: '0 0 4px rgba(251, 191, 36, 0.25)'
            }} data-id="element-3286" data-mp="1gsiqc2q1v47d" />
            </div>

            {steps.map((step, index) => <motion.div key={step.number} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2,
            type: 'spring',
            stiffness: 100
          }} className="relative flex flex-col md:items-center pl-20 md:pl-0 h-full group" data-id="element-3287" data-mp="1pquruzsgfmel" data-mpi={index}>
                {/* Step Number Node */}
                <motion.div animate={{
              scale: [1, 1.04, 1]
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: index * 0.5
            }} className="absolute left-0 md:relative md:left-auto w-16 h-16 md:w-24 md:h-24 bg-stone-50 dark:bg-[#454545] border-4 border-white dark:border-[#2a2a2a] rounded-full flex items-center justify-center mb-8 shadow-[0_0_0_2px_rgba(251,191,36,0.25)] z-10 shrink-0" data-id="element-3288" data-mp="1qknb7tyvfw7w">
                  <span className="font-heading text-2xl md:text-3xl font-extrabold text-[#f59e0b] dark:text-[#fbbf24]" data-id="element-3289" data-mp="1vk8icro73avp">
                    {step.number}
                  </span>
                  {/* Ring trace - desktop */}
                  <div className="hidden md:block" data-id="element-3290" data-mp="k3rz0n9isa3k">
                    <RingTrace elementSize={96} delay={ringParams.ringDelays[index]} duration={ringParams.ringDurations[index]} isInView={stepsInView} startAngle={ringParams.ringAngles[index]} data-id="element-3291" data-mp="p3wjsphgnqzl" />
                  </div>
                  {/* Ring trace - mobile */}
                  <div className="md:hidden" data-id="element-3292" data-mp="ckshyo9j6und">
                    <RingTrace elementSize={64} delay={ringParams.ringDelays[index]} duration={ringParams.ringDurations[index]} isInView={stepsInView} startAngle={ringParams.mobileRingAngles[index]} data-id="element-3293" data-mp="qktks99cwc5h" />
                  </div>
                </motion.div>

                {/* Content Card */}
                <div className="bg-white dark:bg-gradient-to-b dark:from-[#5a5a5a] dark:to-[#4a4a4a] rounded-2xl p-8 w-full md:text-center border border-stone-200 dark:border-[#666666]/60 dark:border-b-[#404040]/60 hover:border-[#fbbf24]/40 dark:hover:border-[#fbbf24]/40 transition-colors duration-300 relative overflow-hidden flex-1 flex flex-col shadow-sm dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_2px_8px_rgba(0,0,0,0.25)]" data-id="element-3294" data-mp="3q070xni8qwlw">
                  <span className="absolute -bottom-6 -right-4 font-heading text-8xl font-black text-stone-100 dark:text-[#5a5a5a]/40 group-hover:text-[#fbbf24]/15 group-hover:rotate-6 transition-all duration-500 pointer-events-none select-none origin-bottom-right" data-id="element-3295" data-mp="36b6dk3r0iuwn">
                    {step.number}
                  </span>
                  <h3 className="font-heading text-2xl font-bold text-stone-900 dark:text-white mb-4 relative z-10" data-id="element-3296" data-mp="2cj5sshwifc2o">
                    {step.title}
                  </h3>
                  <p className="text-stone-800 dark:text-stone-200 leading-relaxed text-[15px] relative z-10" data-id="element-3297" data-mp="2lhhvhiani0bh">
                    {step.description}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-24 bg-[#f5f4f1] dark:bg-[#252525] relative z-10" data-id="element-3298" data-mp="2qinntf0eflkh">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3299" data-mp="112621qpt95ar">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-3300" data-mp="114scby435ywo">
            {/* Image Column */}
            <div className="hidden lg:block relative" data-id="element-3301" data-mp="w5xsqtyao6bi">
              <motion.div initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} className="sticky top-32 aspect-[3/4] rounded-2xl overflow-hidden border border-stone-400 dark:border-[#454443]" data-id="element-3302" data-mp="37rzql9jxv08e">
                <video src="https://cdn.magicpatterns.com/uploads/awwjJQbaUS8dRtKazeNMjW/gravel vid3.mp4" autoPlay loop muted playsInline className="w-full h-full object-cover" data-id="element-3303" data-mp="32xvfce3n461i" />
              </motion.div>
            </div>

            {/* Form Column */}
            <motion.div initial={{
            opacity: 0,
            y: 20,
            rotateY: -5
          }} whileInView={{
            opacity: 1,
            y: 0,
            rotateY: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            ease: 'easeOut'
          }} style={{
            perspective: 1000
          }} className="bg-[#fbf9f6] dark:bg-[#454443] rounded-2xl p-8 md:p-12 border border-stone-300 dark:border-[#555453] shadow-xl" data-id="element-3304" data-mp="2f3c8fmuhkqz2">
              <div className="text-center mb-10" data-id="element-3305" data-mp="bs5w11apm0x2">
                <h2 className="font-heading text-3xl font-bold text-stone-900 dark:text-white mb-4" data-id="element-3306" data-mp="6ue2y3ybw8ra">
                  Apply for a Pro Account
                </h2>
                <p className="text-stone-600 dark:text-stone-200" data-id="element-3307" data-mp="2uvmplhgclus">
                  Fill out the form below and our team will be in touch shortly.
                </p>
              </div>

              {isSubmitted ? <div className="flex flex-col items-center justify-center text-center py-12" data-id="element-3308" data-mp="umbfxydil6tz">
                  <div className="w-16 h-16 bg-[#fbbf24]/15 rounded-full flex items-center justify-center mb-6" data-id="element-3309" data-mp="prp09j0egs43">
                    <CheckCircle2 className="w-8 h-8 text-[#f59e0b] dark:text-[#fbbf24]" data-id="element-3310" data-mp="lq3t8eajr81t" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-stone-900 dark:text-white mb-3" data-id="element-3311" data-mp="1dj46vnz7lvl5">
                    Application Received!
                  </h3>
                  <p className="text-stone-600 dark:text-stone-200 mb-8 max-w-md" data-id="element-3312" data-mp="2w7p1t7nmh6g7">
                    Thank you for applying to the{' '}
                    {`${getRegionMeta().code}Gravel`} Contractor Program. We
                    will review your details and contact you within 24 hours.
                  </p>
                  <button onClick={() => setIsSubmitted(false)} className="px-8 py-3 bg-[#fbbf24] hover:bg-[#f59e0b] text-stone-900 rounded-none font-bold transition-colors" data-id="element-3313" data-mp="3a397h1ecbs5o">
                    Submit Another Application
                  </button>
                </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-3314" data-mp="3ovt5xoqjpbgs">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3315" data-mp="3swmv0lfq4wb9">
                    <div data-id="element-3316" data-mp="3sxy0qqsqce50">
                      <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-3317" data-mp="1qbxgz9xadoo">
                        Company Name *
                      </label>
                      <input type="text" name="companyName" value={formData.companyName} onChange={handleChange} required className={inputClass} data-id="element-3318" data-mp="3p7kf2smpgmp9" />
                    </div>
                    <div data-id="element-3319" data-mp="3k87hbcuapv7w">
                      <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-3320" data-mp="2rznakt8s8xw">
                        Contact Name *
                      </label>
                      <input type="text" name="contactName" value={formData.contactName} onChange={handleChange} required className={inputClass} data-id="element-3321" data-mp="3tz2c55lly34y" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3322" data-mp="31bfzz8ybdq6h">
                    <div data-id="element-3323" data-mp="3g7nkkadmwtma">
                      <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-3324" data-mp="3g4szcgmsr4rt">
                        Email Address *
                      </label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange} required className={inputClass} data-id="element-3325" data-mp="vfbxdvi7uoab" />
                    </div>
                    <div data-id="element-3326" data-mp="y1crpx0vez2c">
                      <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-3327" data-mp="y01jnl0e755f">
                        Phone Number *
                      </label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className={inputClass} data-id="element-3328" data-mp="p0wnglcaznwf" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3329" data-mp="1grklhoiokd5v">
                    <div data-id="element-3330" data-mp="1gtnn75t73uqf">
                      <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-3331" data-mp="1lqe2vs138obb">
                        Contractor License # (Optional)
                      </label>
                      <input type="text" name="licenseNumber" value={formData.licenseNumber} onChange={handleChange} className={inputClass} data-id="element-3332" data-mp="1c0uhaelzqgcr" />
                    </div>
                    <div data-id="element-3333" data-mp="1zp5cp28x3ntn">
                      <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-3334" data-mp="24h57kga4rqjd">
                        Estimated Monthly Volume *
                      </label>
                      <select name="estimatedVolume" value={formData.estimatedVolume} onChange={handleChange} required className={inputClass + ' appearance-none'} data-id="element-3335" data-mp="2eb19qfb8txnp">
                        <option value="" disabled data-id="element-3336" data-mp="25b5unzekhiie">
                          Select volume...
                        </option>
                        <option value="1-5" data-id="element-3337" data-mp="20fgtxsqt9vki">1-5 Deliveries</option>
                        <option value="6-15" data-id="element-3338" data-mp="1vf2vn2f0hzzo">6-15 Deliveries</option>
                        <option value="16-30" data-id="element-3339" data-mp="1qn1b6t7ox2yk">16-30 Deliveries</option>
                        <option value="31+" data-id="element-3340" data-mp="1qlq8kwpze191">31+ Deliveries</option>
                      </select>
                    </div>
                  </div>

                  <div className="pt-4" data-id="element-3341" data-mp="1hno2i2re4iew">
                    <button type="submit" disabled={isSubmitting} className="relative w-full inline-flex items-center justify-center px-8 py-4 bg-[#fbbf24] hover:bg-[#f59e0b] text-stone-900 rounded-none font-bold text-lg transition-all group disabled:opacity-70 overflow-hidden" data-id="element-3342" data-mp="1hks287s2lhna">
                      {/* Subtle idle shimmer */}
                      {!isSubmitting && <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -skew-x-12" animate={{
                    x: ['-200%', '200%']
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatDelay: 4,
                    ease: 'easeInOut'
                  }} data-id="element-3343" data-mp="12j220d626f2i" />}

                      {isSubmitting ? <div className="w-6 h-6 border-2 border-stone-900 border-t-transparent rounded-full animate-spin relative z-10" data-id="element-3344" data-mp="jueyhz3j5xr1" /> : <span className="relative z-10 flex items-center" data-id="element-3345" data-mp="orolz97czzc8">
                          Submit Application
                          <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-3346" data-mp="tl9kd2cv6vby" />
                        </span>}
                    </button>
                  </div>
                </form>}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Contact CTA */}
      <section className="py-16 bg-white dark:bg-[#2a2a2a] relative z-10" data-id="element-3347" data-mp="1fb5hzwsgyw35">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3348" data-mp="1b9jz9ut97nl1">
          <motion.div animate={{
          y: [0, -3, 0]
        }} transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="bg-black dark:bg-gradient-to-br dark:from-[#4a4a4a] dark:to-[#3a3a3a] rounded-2xl p-8 md:p-12 border border-black dark:border-[#5a5a5a] text-center shadow-2xl" data-id="element-3349" data-mp="1p7s04dtev91h">
            <h2 className="font-heading text-3xl font-bold text-white mb-4" data-id="element-3350" data-mp="1u4igl294o6nf">
              Need a Custom Quote?
            </h2>
            <p className="text-white/70 dark:text-stone-200 mb-8 max-w-2xl mx-auto" data-id="element-3351" data-mp="22z6newucpwec">
              For orders over 100 yards, complex deliveries, or to set up a
              net-30 account, contact our dispatch team directly.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-3352" data-mp="2cynqd6i7xw6m">
              <a href="tel:206-619-3742" className="flex items-center justify-center px-8 py-4 bg-[#fbbf24] hover:bg-[#f59e0b] text-stone-900 rounded-none font-bold text-lg transition-colors w-full sm:w-auto" data-id="element-3353" data-mp="2wk7hmzsf1899">
                <div className="relative flex items-center justify-center w-5 h-5 mr-2" data-id="element-3354" data-mp="2mk6s2w3ydvwm">
                  <motion.div className="absolute inset-0 rounded-full border border-stone-900" animate={{
                  scale: [1, 1.4],
                  opacity: [0.8, 0]
                }} transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  ease: 'easeOut'
                }} data-id="element-3355" data-mp="2rii2b7y5u8fd" />
                  <Phone className="w-4 h-4 relative z-10" data-id="element-3356" data-mp="23sdzv3wpkepy" />
                </div>
                (206) 619-3742
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Link to Materials Hub */}
      <section className="py-20 bg-stone-50 dark:bg-[#252525] relative z-10" data-id="element-3357" data-mp="1yvnkkisq9n5l">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3358" data-mp="1uqoqnppjhqia">
          <Link to="/#materials" className="inline-flex items-center gap-3 font-heading text-3xl md:text-4xl font-extrabold text-[#f59e0b] dark:text-[#fbbf24] hover:text-[#d97706] dark:hover:text-[#f59e0b] transition-colors group" data-id="element-3359" data-mp="1ust4azd8q3vr">
            Browse Our Materials Hub
            <motion.div animate={{
            x: [0, 4, 0]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-3360" data-mp="28r974f7zfa8m">
              <ArrowRight className="w-8 h-8 group-hover:translate-x-2 transition-transform" data-id="element-3361" data-mp="2inm1lfbcps4f" />
            </motion.div>
          </Link>
          <p className="text-stone-600 dark:text-stone-300 mt-4 text-lg" data-id="element-3362" data-mp="2nhgt34x77be2">
            View our full product catalog with pricing and ordering.
          </p>
        </div>
      </section>
    </main>;
}